import React, { useState } from 'react';
import { Modal, message, Input, Spin, Select } from 'antd';
import { useIntl } from 'react-intl';
import { FolderKanban, Sparkles } from 'lucide-react';
import { WORKFLOW_PROJECT } from '../../utils/constants/config';
import { actionCreateProject } from './ProjectAction';

const { TextArea } = Input;
const { Option } = Select;

/* ── Figma-extracted design tokens ──────────────────────────────── */
const C = {
  teal: '#2a5f5a',
  tealHover: '#245249',
  tealAlpha10: 'rgba(42,95,90,0.1)',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  red500: '#ef4444',
};

const overlayStyle = { backgroundColor: 'rgba(0,0,0,0.5)' };
const modalBodyStyle = { padding: 24 };

const titleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
};

const titleTextStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: C.gray900,
  margin: 0,
  lineHeight: 1,
};

const descStyle = {
  fontSize: 14,
  color: C.gray600,
  margin: 0,
};

const fieldGap = { marginTop: 20 };

const labelStyle = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  color: C.gray900,
  marginBottom: 8,
};

const requiredStar = { color: C.red500, marginLeft: 2 };

const inputStyle = {
  background: C.white,
  border: `1px solid ${C.gray300}`,
  borderRadius: 6,
  color: C.gray900,
  fontSize: 14,
  padding: '8px 12px',
  width: '100%',
};

const textareaStyle = {
  ...inputStyle,
  resize: 'none',
};

const previewCardStyle = {
  background: C.gray50,
  borderRadius: 8,
  padding: 16,
  border: `1px solid ${C.gray200}`,
  marginTop: 20,
};

const previewLabelStyle = {
  fontSize: 12,
  color: C.gray500,
  marginBottom: 8,
};

const previewContentStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};

const previewIconBox = {
  width: 40,
  height: 40,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: C.tealAlpha10,
};

const previewNameStyle = {
  fontWeight: 600,
  color: C.gray900,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const previewDescStyle = {
  fontSize: 14,
  color: C.gray600,
  marginTop: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
};

const previewBadgeStyle = {
  display: 'inline-block',
  marginTop: 8,
  fontSize: 12,
  padding: '4px 8px',
  borderRadius: 4,
  background: C.gray200,
  color: C.gray700,
};

const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  paddingTop: 16,
  borderTop: `1px solid ${C.gray200}`,
  marginTop: 20,
};

const cancelBtnStyle = {
  border: `1px solid ${C.gray300}`,
  background: C.white,
  color: C.gray700,
  height: 36,
  padding: '0 16px',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const createBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: C.teal,
  border: 'none',
  color: C.white,
  height: 36,
  padding: '0 16px',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const CreateProjectModal = (props) => {
  const { visible = true, onCancel, onOk } = props;
  const [processing, setProcessing] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectType, setProjectType] = useState(undefined);
  const [labelingType, setLabelingType] = useState('2D');
  const [workflow, setWorkflow] = useState(WORKFLOW_PROJECT.SINGLE);
  const intl = useIntl();
  const { formatMessage: t } = intl;

  const handleOk = async () => {
    if (processing) return;
    if (!projectName.trim()) {
      message.error('Project name is required');
      return;
    }
    if (!projectType) {
      message.error('Please select a modality type');
      return;
    }

    try {
      setProcessing(true);
      const trimmedName = projectName.trim();
      const dataDTO = {
        name: trimmedName,
        description: projectDescription.trim(),
        workflow: workflow,
        labeling_type: labelingType,
        key: trimmedName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      };

      const { data } = await actionCreateProject(dataDTO);
      setProcessing(false);
      message.success(`${projectName} has been created successfully`);
      if (onOk) onOk(data);
    } catch (error) {
      message.error('Failed to create project');
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    if (processing) return;
    if (onCancel) onCancel();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={576}
      centered
      destroyOnClose
      maskStyle={overlayStyle}
      bodyStyle={modalBodyStyle}
      closable={!processing}
    >
      <Spin spinning={processing}>
        {/* Header */}
        <div>
          <div style={titleRowStyle}>
            <FolderKanban size={20} color={C.teal} />
            <h3 style={titleTextStyle}>Create New Project</h3>
          </div>
          <p style={descStyle}>
            Set up a new annotation project for research purposes
          </p>
        </div>

        {/* Project Name */}
        <div style={fieldGap}>
          <label style={labelStyle}>
            Project Name<span style={requiredStar}>*</span>
          </label>
          <Input
            placeholder="e.g., Chest X-ray Classification"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Description */}
        <div style={fieldGap}>
          <label style={labelStyle}>Description</label>
          <TextArea
            placeholder="Describe the purpose of this annotation project..."
            value={projectDescription}
            onChange={(e) => setProjectDescription(e.target.value)}
            rows={3}
            style={textareaStyle}
          />
        </div>

        {/* Modality Type */}
        <div style={fieldGap}>
          <label style={labelStyle}>
            Modality Type<span style={requiredStar}>*</span>
          </label>
          <Select
            placeholder="Select modality type"
            value={projectType}
            onChange={setProjectType}
            style={{ width: '100%' }}
            size="large"
          >
            <Option value="X-Ray">X-Ray</Option>
            <Option value="CT">CT Scan</Option>
            <Option value="MRI">MRI</Option>
            <Option value="US">Ultrasound</Option>
            <Option value="PET">PET Scan</Option>
            <Option value="Mammography">Mammography</Option>
            <Option value="Mixed">Mixed Modalities</Option>
          </Select>
        </div>

        {/* Labeling Type */}
        <div style={fieldGap}>
          <label style={labelStyle}>
            Labeling Type<span style={requiredStar}>*</span>
          </label>
          <Select
            value={labelingType}
            onChange={setLabelingType}
            style={{ width: '100%' }}
            size="large"
          >
            <Option value="2D">2D</Option>
            <Option value="3D">3D</Option>
          </Select>
        </div>

        {/* Workflow */}
        <div style={fieldGap}>
          <label style={labelStyle}>
            Workflow<span style={requiredStar}>*</span>
          </label>
          <Select
            value={workflow}
            onChange={setWorkflow}
            style={{ width: '100%' }}
            size="large"
          >
            <Option value={WORKFLOW_PROJECT.SINGLE}>Single</Option>
            <Option value={WORKFLOW_PROJECT.TRIANGLE}>Multiple</Option>
          </Select>
        </div>

        {/* Live Preview Card */}
        <div style={previewCardStyle}>
          <div style={previewLabelStyle}>Preview</div>
          <div style={previewContentStyle}>
            <div style={previewIconBox}>
              <FolderKanban size={20} color={C.teal} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={previewNameStyle}>
                {projectName || 'Project Name'}
              </div>
              <div style={previewDescStyle}>
                {projectDescription || 'Project description will appear here'}
              </div>
              {projectType && (
                <span style={previewBadgeStyle}>{projectType}</span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button
            onClick={handleCancel}
            disabled={processing}
            style={cancelBtnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.gray50)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.white)}
          >
            Cancel
          </button>
          <button
            onClick={handleOk}
            disabled={processing}
            style={createBtnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.tealHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
          >
            <Sparkles size={16} />
            Create Project
          </button>
        </div>
      </Spin>
    </Modal>
  );
};

export default CreateProjectModal;
